import { useState, useRef } from 'react'
import styled from 'styled-components'
import { Dropdown, Option } from '@/features/shared/components/Dropdown'
import { CustomQuantityForm } from '@/features/shop/components/ProductQuantityButton/CustomQuantityForm'
import { isUpDownKeyEvent } from '@/features/shared/utils/keyEvents'
import { normalizeUnitLabel } from '@/features/shared/utils/normalizeProduct'
import { ChevronDownIcon, PositiveIcon } from '@shipt/design-system-icons'
import { type Product } from '@/features/shop/services/Product/types'
import { Body } from '@shipt/design-system-typography'
import { Row } from '@shipt/design-system-layouts'
import { VDivider } from '@/features/shared/components/Dividers'
import { Button } from '@shipt/design-system-buttons'
import { DynamicVariationsQuickAddModal } from '@/features/shop/components/Modal/VariationsQuickAddModal/DynamicVariationsQuickAddModal'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'

type Context = 'cart' | 'order'

type Props = {
  context: Context
  product: Product
  onClick: (quantity: number) => void
  value: number
  increment: number
  disabled?: boolean
  isSelectionPartial?: boolean
  isDockedAtc?: boolean
}

export const ProductQuantityButton = ({
  onClick,
  increment,
  disabled = false,
  product,
  context,
  isSelectionPartial = false,
  isDockedAtc = false,
  value: valueProp,
}: Props) => {
  const [isDropdownShown, setIsDropdownShown] = useState(false)
  const [isCustomInputShown, setIsCustomInputShown] = useState(false)
  const { openDialog } = useHandleDialog()

  const productQtyBtnRef = useRef<HTMLButtonElement>(null)

  // If selection is partial and this is not the docked ATC, clear quantity to show the initial "add" state
  // Only applies to variation products — non-variation products should never have their quantity cleared
  const isVariationProduct = !!(product.parentProductId && product.variationSize)
  const value = isVariationProduct && isSelectionPartial ? 0 : valueProp

  const getQuantityIncrements = () => {
    const { type, unitWeight, hasCustomLabel, purchaseLimit } = product
    const quantityIncrements: number[] = []

    let maxQuantity = 10
    if (purchaseLimit) {
      maxQuantity = Math.min(purchaseLimit, maxQuantity)
    }

    for (let i = 1; i <= maxQuantity; i++) {
      if (type === 'by weight' && !hasCustomLabel) {
        const byWeightIncrement = i * unitWeight * increment
        quantityIncrements.push(byWeightIncrement)
      } else if (i * increment <= maxQuantity) quantityIncrements.push(i * increment)
    }
    return quantityIncrements
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (isUpDownKeyEvent(e)) {
      if (value > 0) {
        e.preventDefault()
        setIsDropdownShown(true)
      }
    }
  }

  const handleButtonClick = () => {
    // Docked ATC for variation products opens the quick-add modal
    if (isDockedAtc && (product.variationSize || product.parentProductId)) {
      openDialog(DynamicVariationsQuickAddModal, {
        product,
      })
      return
    }
    // toggle dropdown if quantity exists
    if (value > 0) {
      setIsDropdownShown(true)
    } else {
      handleInitialIncrement()
    }
  }

  const handleInitialIncrement = () => {
    const initialIncrement = getQuantityIncrements()[0]
    if (initialIncrement !== undefined) {
      handleQuantityChange(initialIncrement.toString())
    }
  }

  const handleQuantityChange = (quantity: string) => {
    if (quantity === 'custom') {
      setIsCustomInputShown(true)
    } else {
      setIsCustomInputShown(false)
      onClick(Number(quantity))
    }
  }

  const handleCloseDropdown = () => {
    productQtyBtnRef.current?.focus()
    setIsDropdownShown(false)
  }

  const renderOptionLabel = (quantity: number) => {
    return product.unitLabel
      ? `${normalizeUnitLabel(product, quantity)} in ${context}`
      : `in ${context}`
  }

  const qtyIncrements = getQuantityIncrements()
  const { purchaseLimit } = product
  return isCustomInputShown ? (
    <CustomQuantityForm
      onSubmit={handleQuantityChange}
      context={context}
      btnLabel={`Update ${context}`}
      defaultValue={value}
      step={qtyIncrements[0]}
      limit={purchaseLimit}
    />
  ) : (
    <QuantityButtonWrapper onKeyDown={handleKeyDown}>
      <QuantityButton
        disabled={disabled}
        fullWidth
        onClick={handleButtonClick}
        ref={productQtyBtnRef}
        value={value}
        aria-label={value === 0 ? `Add to ${context}` : `${value} in ${context}`}
        {...(value > 0 && {
          'aria-haspopup': 'listbox',
          'aria-expanded': isDropdownShown,
        })}
      >
        {value > 0 && (
          <>
            <Row align="center">
              <DownArrow inherit />
              <QuantityWrapper>
                <Body variant="secondary" surface="inverse" strong size="sm">
                  QTY
                </Body>
                <Body surface="inverse" size="md" strong>
                  {value}
                </Body>
              </QuantityWrapper>
            </Row>
            <ButtonDivider />
          </>
        )}
        <QuantityContextWrapper surface="inverse" strong>
          {value === 0 ? `Add to ${context}` : `In ${context}`}
        </QuantityContextWrapper>
      </QuantityButton>
      {value > 0 && (
        <ProductQuantityDropdown
          isOpen={isDropdownShown}
          onChange={handleQuantityChange}
          onClose={handleCloseDropdown}
          value={value}
          buttonRef={productQtyBtnRef}
        >
          {!qtyIncrements.includes(value) && (
            <ButtonOption aria-label={`checkmark ${value}`} value={value} selected>
              <Checkmark size="md" />
              <OptionText>{value}</OptionText> {renderOptionLabel(value)}
            </ButtonOption>
          )}
          {qtyIncrements.map((qtyIncrement) => {
            const isSelected = qtyIncrement === value
            return (
              <ButtonOption
                key={qtyIncrement}
                aria-label={isSelected ? `checkmark ${qtyIncrement}` : qtyIncrement}
                value={qtyIncrement}
                selected={isSelected}
              >
                {!!isSelected && <Checkmark size="md" />}
                <OptionText>{qtyIncrement}</OptionText> {renderOptionLabel(qtyIncrement)}
              </ButtonOption>
            )
          })}
          {purchaseLimit > 10 && <CustomOption value="custom" label="Custom Amount" />}
          <RemoveOption value={0} label={`Remove from ${context}`} />
        </ProductQuantityDropdown>
      )}
    </QuantityButtonWrapper>
  )
}

const ProductQuantityDropdown = styled(Dropdown)`
  top: 2px;
  overflow: hidden;
`

const QuantityButtonWrapper = styled.div`
  position: relative;
  width: 100%;
`

const QuantityButton = styled(Button)<{ value: number }>`
  padding: ${({ value }) => (value > 0 ? '0.3125rem 0' : null)};

  .buttonContent {
    flex-grow: 1;
    display: flex;
    align-items: center;
  }
`

const DownArrow = styled(ChevronDownIcon)`
  margin: 0 0.5rem;
`

const Checkmark = styled(PositiveIcon)`
  position: absolute;
  left: 6px;
`

const ButtonDivider = styled(VDivider)`
  height: 1.5rem;
  margin-left: 1rem;
  opacity: 0.7;
  background-color: ${({ theme }) => theme.white};
`

const OptionText = styled.span`
  display: inline-block;
  width: 2.25rem;
`

const ButtonOption = styled(Option)`
  display: flex;
  padding: 0.5rem 0 0.5rem 2rem;
`

const CustomOption = styled(ButtonOption)`
  color: ${({ theme }) => theme.purple500};
`

const RemoveOption = styled(ButtonOption)`
  color: ${({ theme }) => theme.red500};
`

const QuantityWrapper = styled.div`
  width: 80%;
`

const QuantityContextWrapper = styled(Body)`
  margin: 0 auto;
`
